import React, { useState, useEffect } from 'react';
import { storyblokEditable } from 'gatsby-source-storyblok';
import { getQbankImageId, getResponsiveImageUrl } from '../../utils/imageUrls';
import UtilityButton from '../buttons/utilityButton';
import { useTranslation } from 'react-i18next';
import { formatDate, formatTime } from '../../utils/dateTime';
import CalendarIcon from '../atoms/icons/calendar';
import { getSiteConfig } from '../../../siteConfig';

const ActivityList = ({
  blok,
  destination,
  fromDate = '2024-11-28',
  toDate = '2025-10-28',
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [activities, setActivities] = useState([]);

  const { t } = useTranslation(['labels']);

  useEffect(() => {
    const getActivities = async () => {
      const res = await fetch(
        `${
          getSiteConfig().ethelBookingURL
        }/api/v1/query/destination/addonContract/schemeActivities?destinationId=${destination}&marketId=${
          getSiteConfig().ethelMarket
        }&languageId=${
          getSiteConfig().ethelLanguage
        }&FromDate=${fromDate}&ToDate=${toDate}`,
      );
      const { data } = await res.json();

      setActivities(data);
      setIsLoading(false);
    };
    getActivities();
  }, []);

  const days = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ];

  return (
    // Important! Always set the container height explicitly
    <section
      className={`grid grid-cols-subgrid col-span-12 gap-4 max-w-screen-xl`}
      {...storyblokEditable(blok)}
      id={blok.name}
      key={blok._uid}
    >
      {isLoading && (
        <div className="col-span-12 grid grid-cols-subgrid ">
          <div className="col-span-12 md:col-span-4 aspect-4/3 bg-black-12.5 rounded-lg animate-pulse"></div>
          <div className="col-span-12 md:col-span-4 bg-black-12.5 rounded-lg animate-pulse"></div>
          <div className="col-span-12 md:col-span-4 bg-black-12.5 rounded-lg animate-pulse"></div>
        </div>
      )}
      {!isLoading &&
        activities.map((a) => {
          const id = getQbankImageId(a.imageUrl);
          const imageUrlParams = {
            imageId: id,
            parentWidth: 'w-1/4',
          };

          const eventDate = new Date(a.dateTime);

          return (
            <section className="flex flex-wrap scroll-mt-12  justify-center col-span-12 md:col-span-6 lg:col-span-4 h-auto bg-yellow-15">
              <div className="w-full relative">
                <div
                  className="h-full items-end grid-rows-[auto,auto,1fr] w-full grid grid-cols-12 max-w-screen-xl gap-x-4 mx-auto"
                  id="04e70bf2-ee32-4612-a6f1-5901e86a53b8"
                >
                  <div className="card   col-span-12 aspect-4/3 bg-black-12.5 max-w-screen-xl rounded relative flex flex-col justify-normal flex-wrap">
                    <picture className="flex h-full w-full absolute z-0 rounded">
                      <source
                        media="(max-width: 640px)"
                        srcSet={getResponsiveImageUrl({
                          screenWidth: '640px',
                          forceAspect: 'aspect-4/3',
                          ...imageUrlParams,
                        })}
                      />
                      <source
                        media="(max-width: 768px)"
                        srcSet={getResponsiveImageUrl({
                          screenWidth: '768px',
                          forceAspect: 'aspect-4/3',
                          ...imageUrlParams,
                        })}
                      />
                      <source
                        media="(max-width: 1024px)"
                        srcSet={getResponsiveImageUrl({
                          screenWidth: '1024px',
                          forceAspect: false,
                          ...imageUrlParams,
                        })}
                      />
                      <source
                        media="(min-width: 1024px)"
                        srcSet={getResponsiveImageUrl({
                          screenWidth: '1440px',
                          forceAspect: false,
                          ...imageUrlParams,
                        })}
                      />
                      <img
                        loading="lazy"
                        src={getResponsiveImageUrl({
                          screenWidth: '640px',
                          forceAspect: true,
                          ...imageUrlParams,
                        })}
                        className="object-cover w-full rounded object-center"
                      />
                    </picture>
                    <div className="  flex flex-row flex-wrap z-20 py-2 md:py-4 divide-none sm:divide-solid divide-black-30 divide-x md:divide-none xl:divide-solid">
                      <div className="z-20 flex 0 items-center rounded flex-nowrap items-center w-max ml-2 my-0 md:ml-4 bg-yellow-15  p-2 md:px-4 ">
                        <span className="mr-2 relative text-xxs lg:text-xs">
                          <CalendarIcon />
                        </span>
                        <span>
                          {t(days[eventDate.getDay()])} {formatTime(eventDate)}{' '}
                          <span className="font-bold text-xs lg:text-sm"></span>
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="card   col-span-12 h-auto   bg-yellow-15 max-w-screen-xl rounded relative flex flex-col justify-normal flex-wrap">
                    <div className="z-20  flex flex-col ">
                      <div className="px-2 md:px-4  z-20">
                        <div className="my-2 md:my-4 px-4 md:px-4 py-2 md:py-4 is-nested rounded w-full col-span-12 bg-transparent text-box">
                          <h3>
                            <b>{a.description}</b>
                          </h3>
                          <p>
                            <span className="text-sm">{a.info}</span>
                          </p>
                          {/*                           <p>
                            <i>
                              <span class="text-sm">
                                För barn erbjuder vi Canvas &amp; Cola.
                              </span>
                            </i>
                          </p>
 */}{' '}
                        </div>
                      </div>
                    </div>
                  </div>
                  {a.price && a.cancelled !== false && (
                    <div className="card   col-span-12 h-auto   bg-yellow-15 max-w-screen-xl rounded relative flex flex-col justify-normal flex-wrap">
                      <div className="z-20  flex flex-col ">
                        <BookNowButton activity={a} />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </section>
          );
        })}
    </section>
  );
};

const BookNowButton = ({ activity }) => {
  const { t } = useTranslation(['booking']);

  const label = t('StartingPrice', {
    price: activity.price,
  });

  return (
    <div className="col-span-12 px-2 px-4">
      <span className="text-xxs py-2 px-4 md:px-4">
        {activity.ageFrom !== null && activity.ageFrom > 0 && (
          <span className="info">Minimiålder {activity.ageFrom} år.</span>
        )}
        {activity.allotmentLeft !== null &&
          activity.allotmentLeft > 0 &&
          activity.allotmentLeft < 10 && (
            <span className="info">
              Endast {activity.allotmentLeft} platser kvar
            </span>
          )}
        {t('Book before')} {formatDate(activity.dueDate)}{' '}
        {formatTime(activity.dueDate)}
      </span>
      <UtilityButton label={label} link={{ url: '' }}></UtilityButton>
    </div>
  );
};

export default ActivityList;

/*           return (
            <div
              className={`col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 bg-yellow-30 rounded-lg grid grid-rows-card grid-cols-subgrid grid-flow-col`}
            >
              <div
                className={`col-span-12 relative aspect-4/3 rounded-lg bg-black-12.5 ${
                  isLoading ? 'animate-pulse' : ''
                }`}
              >
                <picture className="rounded-lg aspect-video">
                  <source
                    media="(max-width: 640px)"
                    srcset={getResponsiveImageUrl({
                      screenWidth: '640px',
                      forceAspect: 'aspect-4/3',
                      ...imageUrlParams,
                    })}
                  />
                  <source
                    media="(max-width: 768px)"
                    srcset={getResponsiveImageUrl({
                      screenWidth: '768px',
                      forceAspect: 'aspect-4/3',
                      ...imageUrlParams,
                    })}
                  />
                  <source
                    media="(max-width: 1024px)"
                    srcset={getResponsiveImageUrl({
                      screenWidth: '1024px',
                      forceAspect: 'aspect-4/3',
                      ...imageUrlParams,
                    })}
                  />
                  <source
                    media="(min-width: 1024px)"
                    srcset={getResponsiveImageUrl({
                      screenWidth: '1440px',
                      forceAspect: 'aspect-4/3',
                      ...imageUrlParams,
                    })}
                  />
                  <img
                    loading="lazy"
                    src={getResponsiveImageUrl({
                      screenWidth: '640px',
                      forceAspect: 'aspect-4/3',
                      ...imageUrlParams,
                    })}
                    className="object-cover w-full rounded object-center"
                  />
                </picture>
                <div className="absolute bg-yellow-15 mx-2 md:mx-4 p-2 md:px-4 top-2 md:top-4 rounded flex flex-nowrap items-center">
                  <span className="mr-2 relative text-xxs lg:text-xs">
                    <CalendarIcon />
                  </span>
                  <span>
                    {t(days[eventDate.getDay()])} {formatTime(eventDate)}
                  </span>
                </div>
              </div>
              <div className="col-span-12 relative px-4 md:px-8 py-4">
                <h3>{a.description}</h3>
                <p className="text-sm">{a.info}</p>
              </div>
              {a.price && a.cancelled !== false && (
                <BookNowButton activity={a} />
              )}
            </div>
          ); 
 
 )} */
